import React, { useState } from "react";

import NumberOptionList from "@/app/components/numberOptionList";
import checkAddToCart, { AddToCartStatus } from "@/app/ec/product/[basePath]/checkAddToCart";
import PriceBreakSummary from "@/app/ec/product/[basePath]/priceBreakSummary";
import {
  AddToAutoOrderProps,
  AutoOrderFrequencyLevels,
  AutoOrderType,
  getSelectedAutoOrderType,
} from "@/app/ec/product/callToAction";
import ProductImage from "@/app/ec/product/productImage";
import Button from "@/components/design-system/button";
import Dropdown from "@/components/design-system/dropdown";

import fixImageURI from "@/app/ec/product/fixImageURI";
import styles from "./pinnedAddToCart.module.scss";

function PinnedAddToCart({
  quantity,
  setQuantity,
  maxInventoryAvailable,
  vendorOptions,
  mediaPath,
  title,
  bestPrice,
  addToCartCallback,
  productBeingAddedToCart,
  addToAutoOrderCallback,
  vendorProductBeingAddedToCart,
  vendorOption,
  chosenAutoOrderLevel,
  // setChosenAutoOrderLevel,
  showPinnedAddToCart,
  chosenVendorKey,
  // url,
  enableErrorAddToCartCallback,
}: PinnedAddToCartProps) {
  const [subscribingToAutoOrder, setSubscribingToAutoOrder] = useState(false);

  if (vendorOptions.length === 0) {
    return <div className={styles.addToCart} />;
  }

  const onSubmitHandler = (e: React.FormEvent<HTMLElement>) => {
    e.preventDefault();
    handleSubmission();
  };

  const handleSubmission = async () => {
    if (productBeingAddedToCart === false) {
      if (chosenAutoOrderLevel === AutoOrderType.ONE_TIME.index) {
        if (
          checkAddToCart(
            quantity,
            vendorOptions[chosenVendorKey].minimumQuantityPurchasable,
            vendorOptions[chosenVendorKey].inventory,
          ) === AddToCartStatus.OK
        ) {
          addToCartCallback(vendorOptions[chosenVendorKey]);
        } else {
          window.location.href = "#pdp-product-image";
          enableErrorAddToCartCallback();
        }
      } else {
        setSubscribingToAutoOrder(true);
        const autoOrderLevel: AutoOrderFrequencyLevels = getSelectedAutoOrderType(chosenAutoOrderLevel)
          .frequenceId as AutoOrderFrequencyLevels;
        await addToAutoOrderCallback(
          {
            frequencyId: autoOrderLevel,
            quantity: quantity,
          },
          "Pinned Add to Cart",
        );
        setSubscribingToAutoOrder(false);
      }
    }
  };

  const chosenProductBeingAddedToCart =
    vendorOptions[chosenVendorKey] !== undefined &&
    vendorProductBeingAddedToCart === vendorOptions[chosenVendorKey].vendorProductId;
  const isBusy = chosenProductBeingAddedToCart || subscribingToAutoOrder;

  return (
    <section className={`${styles.PinnedAddToCart} ${showPinnedAddToCart ? styles.show : ""}`}>
      <div className={styles.pinnedAddToCart}>
        <div className={styles.productInfo}>
          <div className={styles.productImage}>
            <ProductImage src={mediaPath ? fixImageURI(mediaPath) : ""} alt="" height={60} width={60} />
          </div>
          <div className={styles.productTitle}>
            <span>{title}</span>
          </div>
        </div>
        <div className={styles.bestPrice}>
          <PriceBreakSummary
            vendorOption={vendorOption}
            className="p-0"
            retailPrice={bestPrice}
            eachClassName={styles.eachSuffix}
            shippingClassName={styles.pinnedShippingMessage}
            twoDayBadgeTooltipPosition="bottom"
            showSavings={false}
          />
        </div>
        <form className={styles.addToCart} onSubmit={onSubmitHandler}>
          <div className={styles.quantity}>
            <Dropdown
              value={String(quantity)}
              placeholder="Quantity"
              onChange={(event) => setQuantity(parseInt(event.target.value))}
            >
              <NumberOptionList start={1} end={maxInventoryAvailable} />
            </Dropdown>
          </div>
          <Button
            variant="primary"
            color="teal"
            type="submit"
            showRightIcon={false}
            loading={isBusy}
            disabled={isBusy}
            className={styles.addToCartButton}
            text={chosenAutoOrderLevel == 0 ? "Add to Cart" : "Add to Auto Order"}
          />
        </form>
      </div>
    </section>
  );
}

type PinnedAddToCartProps = {
  quantity: number;
  setQuantity: React.Dispatch<React.SetStateAction<number>>;
  maxInventoryAvailable: number;
  vendorOptions: VendorOptionState[];
  mediaPath: string;
  title: string;
  bestPrice: number;
  addToCartCallback: (vendorProductToAdd: VendorOptionState) => void;
  addToAutoOrderCallback: (
    productToSubscribe: Pick<AddToAutoOrderProps, "frequencyId" | "quantity">,
    source: string,
  ) => void;
  productBeingAddedToCart: boolean;
  vendorProductBeingAddedToCart: null | number;
  vendorOption?: VendorOptionState;
  chosenAutoOrderLevel: number;
  setChosenAutoOrderLevel: React.Dispatch<React.SetStateAction<number>>;
  showPinnedAddToCart: boolean;
  chosenVendorKey: number;
  url: string;
  enableErrorAddToCartCallback: () => void;
};

export default React.memo(PinnedAddToCart);
